<template>
  <section id="ar-preview" class="page-section asset-preview-section selection-section">
    <div class="section-header">
      <h3>Select the 3D AR object to be shown</h3>
    </div>

    <div class="ar-tracking-type-selector">
      <div class="tracking-type-options">
        <!-- Surface Tracking temporarily hidden — currently buggy.
        <button
          type="button"
          :class="['tracking-type-btn', { 'tracking-type-btn--active': arTrackingType === 'surface' }]"
          @click="handleSurfaceClick"
        >
          <span class="tracking-type-btn-title">Surface Tracking</span>
          <span class="tracking-type-btn-desc">Point at any flat surface</span>
        </button>
        -->
        <button
          type="button"
          :class="['tracking-type-btn', { 'tracking-type-btn--active': arTrackingType === 'marker' }]"
          @click="handleMarkerClick"
        >
          <span class="tracking-type-btn-title">Marker Tracking</span>
        </button>
      </div>
    </div>

    <!-- Hidden file input stays in the section so it can be triggered from
         the marker modal regardless of modal teleport. -->
    <input
      ref="markerFileInput"
      type="file"
      accept="image/png,image/jpeg,image/webp"
      class="visually-hidden"
      @change="handleMarkerImageSelected"
    />

    <div class="phone-preview-shell">
      <div class="phone-preview">
        <div class="phone-speaker"></div>
        <div class="phone-screen phone-screen--asset" :style="phoneScreenStyle">
          <img :src="arSceneImage" alt="AR scene preview" class="scene-image" />
          <div class="asset-overlay">
            <button
              type="button"
              :class="['asset-overlay-trigger', { 'preview-target--configured': hasCustomizedArAsset }]"
              :disabled="!hasTemplateSelection"
              aria-label="Choose a 3D object"
              @click="openAssetActionModal"
            >
              <svg class="marching-ants marching-ants--panel" viewBox="0 0 220 260" preserveAspectRatio="none" aria-hidden="true">
                <rect x="1" y="1" width="218" height="258" rx="18" ry="18" pathLength="100" />
              </svg>
              <img
                v-if="arAssetPreviewInfo?.type === 'default' && arAssetPreviewInfo.thumbnail"
                :src="arAssetPreviewInfo.thumbnail"
                :alt="arAssetPreviewInfo.name || 'Selected 3D model'"
                class="asset-image"
              />
              <div v-else-if="arAssetPreviewInfo" class="asset-chip">
                {{ arAssetPreviewInfo.name || 'Custom 3D model selected' }}
              </div>
              <div v-else class="asset-placeholder">
                Tap to choose a 3D Object
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="isAssetActionModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeAssetActionModal"
      >
        <div
          class="asset-action-modal asset-action-modal--hub"
          role="dialog"
          aria-modal="true"
          aria-label="AR asset actions"
        >
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close AR asset actions"
              @click="closeAssetActionModal"
            >
              ×
            </button>
            <h3>3D AR Object</h3>
          </div>
          <div class="modal-body modal-body--hub">
            <div class="hub-block">
              <p class="studio-block-title">3D Object</p>
              <p class="hub-block-copy">Choose the AR object you want to use</p>
              <div class="modal-actions">
                <button type="button" class="action-button" @click="handleAction('browse')">
                  Browse Assets
                </button>
                <button
                  v-if="hasArAsset"
                  type="button"
                  class="action-button"
                  @click="handleAction('clear')"
                >
                  Remove asset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Marker tracking modal: marker image upload + entry point to the
           3D asset picker. Opens when the Marker Tracking button is clicked. -->
      <div
        v-if="isMarkerModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeMarkerModal"
      >
        <div
          class="asset-action-modal asset-action-modal--hub asset-action-modal--marker"
          role="dialog"
          aria-modal="true"
          aria-label="Marker tracking setup"
        >
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close marker tracking setup"
              @click="closeMarkerModal"
            >
              ×
            </button>
            <h3>Marker Tracking</h3>
          </div>
          <div class="modal-body modal-body--hub">
            <div class="hub-block">
              <p class="studio-block-title">Marker Image</p>
              <p class="hub-block-copy">
                Upload the image your users will point their camera at to trigger the AR experience.
              </p>

              <div v-if="!markerSourcePreview" class="marker-upload-zone" @click="triggerMarkerFileInput">
                <div class="marker-upload-placeholder">
                  <span class="marker-upload-icon">+</span>
                  <span>Upload marker image</span>
                  <span class="marker-upload-hint">PNG, JPEG or WebP, max 5MB</span>
                </div>
              </div>

              <div v-else class="marker-preview-container">
                <img :src="markerSourcePreview" alt="Marker target preview" class="marker-preview-image" />
                <div class="marker-preview-info">
                  <div v-if="markerCompileStatus === 'validating'" class="marker-compile-progress">
                    <span class="progress-text">Validating image with content moderation...</span>
                  </div>
                  <div v-else-if="markerCompileStatus === 'compiling'" class="marker-compile-progress">
                    <div class="progress-bar-track">
                      <div class="progress-bar-fill" :style="{ width: markerCompileProgress + '%' }"></div>
                    </div>
                    <span class="progress-text">Compiling target... {{ markerCompileProgress }}%</span>
                  </div>
                  <div v-else-if="markerCompileStatus === 'uploading-mind'" class="marker-compile-progress">
                    <span class="progress-text">Uploading compiled target...</span>
                  </div>
                  <div v-else-if="markerCompileStatus === 'ready'" class="marker-compile-success">
                    Target approved and ready
                  </div>
                  <div v-else-if="markerCompileStatus === 'error'" class="marker-compile-error">
                    {{ markerCompileError }}
                  </div>
                  <div class="marker-preview-actions">
                    <button
                      type="button"
                      class="marker-change-btn"
                      :disabled="markerBusy"
                      @click="triggerMarkerFileInput"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      class="marker-remove-btn"
                      :disabled="markerBusy"
                      @click="removeMarkerImage"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              <details class="marker-guide">
                <summary class="marker-guide-summary">What makes a good marker image?</summary>
                <div class="marker-guide-body">
                  <ul class="marker-guide-list">
                    <li><strong>High contrast</strong> — crisp edges, bold tones, not washed out.</li>
                    <li><strong>Asymmetric detail</strong> — the tracker needs a unique orientation to lock onto.</li>
                    <li><strong>No glare or reflections</strong> — glossy packaging and laminated cards fail under lights.</li>
                    <li><strong>Avoid large plain areas</strong> — solid backgrounds give the tracker nothing to grip.</li>
                    <li><strong>Avoid pure text</strong> — repeating letterforms confuse feature detection.</li>
                    <li><strong>Keep it flat</strong> — crumpled paper, curved surfaces, and tilted photos all hurt tracking.</li>
                  </ul>
                  <p class="marker-guide-footnote">Logos, product photos with texture, and illustrated cards all work well.</p>
                </div>
              </details>
            </div>

            <div class="hub-block">
              <p class="studio-block-title">3D Object</p>
              <p class="hub-block-copy">Choose the AR object that appears when the marker is detected.</p>
              <div class="modal-actions">
                <button type="button" class="action-button" @click="openAssetModalFromMarker">
                  Choose 3D asset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useUIStore } from '@/store/ui.store'
import { useCampaignBuilderStore } from '@/store/campaignBuilder.store'
import arSceneImage from '@/assets/Campaign Creator Assets/ar-preview/shop-example.png'

type TemplateConfigLike = { experienceType?: string | null } | null | undefined

type ArAssetPreviewInfo =
  | { type: 'default'; thumbnail?: string | null; name?: string | null }
  | { type: 'staged'; name?: string | null }

type AssetModalAction = 'upload' | 'browse' | 'clear'

const MAX_MARKER_SIZE = 5 * 1024 * 1024 // 5MB

const props = defineProps<{
  templateConfig?: TemplateConfigLike
  arAssetPreviewInfo?: ArAssetPreviewInfo | null
  logoPreviewUrl?: string | null
  backgroundPreviewUrl?: string | null
  arTrackingType?: 'surface' | 'marker'
  brandColors: {
    background: string
    header: string
    secondary: string
    buttonText: string
  }
}>()

const emit = defineEmits<{
  (e: 'open-upload-modal', payload: { type: 'ar' }): void
  (e: 'open-asset-modal', payload: { type: 'ar' }): void
  (e: 'clear-asset', type: 'ar'): void
  (e: 'set-ar-tracking-type', type: 'surface' | 'marker'): void
}>()

const uiStore = useUIStore()
const campaignStore = useCampaignBuilderStore()
const isAssetActionModalOpen = ref(false)
const isMarkerModalOpen = ref(false)
const markerFileInput = ref<HTMLInputElement | null>(null)

const openMarkerModal = () => {
  isMarkerModalOpen.value = true
}
const closeMarkerModal = () => {
  isMarkerModalOpen.value = false
}

const handleSurfaceClick = () => {
  emit('set-ar-tracking-type', 'surface')
  openAssetActionModal()
}
const handleMarkerClick = () => {
  emit('set-ar-tracking-type', 'marker')
  openMarkerModal()
}
const openAssetModalFromMarker = () => {
  closeMarkerModal()
  openAssetActionModal()
}

// Preview source precedence:
//   1. fresh local blob (sourceImagePreview) — selected this session
//   2. persisted S3 URL (sourceImageUrl) — so edit-mode + reload show the
//      current marker instead of an empty upload zone
const markerSourcePreview = computed(() =>
  campaignStore.markerTarget.sourceImagePreview || campaignStore.markerTarget.sourceImageUrl || null
)
const markerCompileStatus = computed(() => campaignStore.markerTarget.compileStatus)
const markerCompileProgress = computed(() => campaignStore.markerTarget.compileProgress)
const markerCompileError = computed(() => campaignStore.markerTarget.compileError)
// True while the marker is being validated/compiled/uploaded. Used to
// disable both the Change and Remove buttons so the user can't swap
// out mid-compile and leave the store in a half-written state.
const markerBusy = computed(() =>
  ['validating', 'compiling', 'uploading-mind'].includes(markerCompileStatus.value as string)
)

function triggerMarkerFileInput() {
  markerFileInput.value?.click()
}

function handleMarkerImageSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  input.value = ''

  if (file.size > MAX_MARKER_SIZE) {
    campaignStore.setMarkerCompileError('Image must be under 5MB')
    return
  }

  // Stage only — the actual Rekognition validate + MindAR compile + .mind
  // upload now run as part of the submission pipeline in FormView, so we
  // know the user is authenticated before hitting those services. The
  // preview shows the local blob immediately.
  const preview = URL.createObjectURL(file)
  campaignStore.setMarkerSourceImage(file, preview)
}

function removeMarkerImage() {
  campaignStore.clearMarkerTarget()
}

const hasTemplateSelection = computed(() => !!props.templateConfig?.experienceType)
const hasArAsset = computed(() => !!props.arAssetPreviewInfo)
const hasCustomizedArAsset = computed(() => hasArAsset.value)
const phoneScreenStyle = computed(() => ({
  backgroundColor: props.brandColors.background,
  backgroundImage: props.backgroundPreviewUrl ? `url('${props.backgroundPreviewUrl}')` : undefined,
  backgroundSize: props.backgroundPreviewUrl ? 'cover' : undefined,
  backgroundPosition: props.backgroundPreviewUrl ? 'center' : undefined,
  backgroundRepeat: props.backgroundPreviewUrl ? 'no-repeat' : undefined,
}))

const openAssetActionModal = () => {
  if (!hasTemplateSelection.value) {
    return
  }
  isAssetActionModalOpen.value = true
}

const closeAssetActionModal = () => {
  isAssetActionModalOpen.value = false
}

const handleAction = (action: AssetModalAction) => {
  if (action === 'upload') {
    emit('open-upload-modal', { type: 'ar' })
  } else if (action === 'browse') {
    emit('open-asset-modal', { type: 'ar' })
  } else {
    emit('clear-asset', 'ar')
  }

  closeAssetActionModal()
}

watch(() => isAssetActionModalOpen.value, (isOpen) => {
  if (isOpen) {
    uiStore.openModal()
  } else {
    uiStore.closeModal()
  }
})

onBeforeUnmount(() => {
  if (isAssetActionModalOpen.value) {
    uiStore.closeModal()
  }
})
</script>

<style scoped>
.ar-tracking-type-selector {
  margin-bottom: 1.5rem;
  width: min(100%, 560px);
  margin-inline: auto;
}
.tracking-type-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--color-text-secondary, #aaa);
  text-align: center;
}
.tracking-type-options {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}
/* Pastel green fill with dark text (IRL2-2900). Single tracking option
   (Surface hidden), so the button reads green in every state — active just
   deepens the green rather than switching to blue. Shape language matches
   splash how-it-works / get-started buttons. */
.tracking-type-btn {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: var(--base-layout-button-padding-lg);
  border: 1px solid #a8e6cf;
  border-radius: var(--base-layout-button-border-radius-lg);
  background: #a8e6cf;
  color: var(--color-background);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  max-width: 240px;
  font-weight: var(--base-layout-button-font-weight);
}
.tracking-type-btn:hover {
  background: #8fdcbf;
  border-color: #8fdcbf;
}
.tracking-type-btn--active,
.tracking-type-btn--active:hover {
  background: #6fd3a6;
  color: var(--color-background);
  border-color: #6fd3a6;
}
.tracking-type-btn-title {
  font-size: var(--base-layout-button-font-size-md);
  font-weight: 700;
}
.tracking-type-btn-desc {
  font-size: 0.78rem;
  opacity: 0.85;
}
.marker-target-section {
  margin-bottom: 1.5rem;
  padding: 0 1rem;
}
.marker-target-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--color-text-secondary, #aaa);
}
.marker-target-desc {
  font-size: 0.78rem;
  color: var(--color-text-muted, #888);
  margin-bottom: 0.75rem;
}
.marker-upload-zone {
  border: 2px dashed rgba(var(--vt-c-accent-rgb), 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}
.marker-upload-zone:hover {
  border-color: rgba(var(--vt-c-accent-rgb), 0.6);
  background: rgba(var(--vt-c-accent-rgb), 0.03);
}
.marker-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  color: #ccc;
  font-size: 0.85rem;
}
.marker-upload-icon {
  font-size: 1.8rem;
  line-height: 1;
  color: var(--vt-c-accent);
}
.marker-upload-hint {
  font-size: 0.7rem;
  opacity: 0.6;
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.marker-preview-container {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0.75rem;
}
.marker-preview-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.marker-preview-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.marker-compile-progress {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.progress-bar-track {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: var(--vt-c-accent);
  border-radius: 3px;
  transition: width 0.3s ease;
}
.progress-text {
  font-size: 0.75rem;
  color: #aaa;
}
.marker-compile-success {
  font-size: 0.8rem;
  color: var(--vt-c-accent);
  font-weight: 500;
}
.marker-compile-error {
  font-size: 0.8rem;
  color: #ff6b6b;
}
.marker-preview-actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.marker-change-btn,
.marker-remove-btn {
  background: none;
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.marker-change-btn {
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.5);
  color: var(--vt-c-accent);
}
.marker-change-btn:hover:not(:disabled) {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
  border-color: var(--vt-c-accent);
}
.marker-remove-btn {
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff6b6b;
}
.marker-remove-btn:hover:not(:disabled) {
  background: rgba(255, 107, 107, 0.1);
  border-color: #ff6b6b;
}
.marker-change-btn:disabled,
.marker-remove-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Collapsible good-marker guide. Uses native <details> so it needs no
   JS and preserves state. Compact by default so it doesn't crowd the
   upload UI. */
.marker-guide {
  margin-top: 0.9rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.18);
  border-radius: 10px;
  background: rgba(var(--vt-c-accent-rgb), 0.04);
}
.marker-guide[open] {
  background: rgba(var(--vt-c-accent-rgb), 0.06);
}
.marker-guide-summary {
  cursor: pointer;
  padding: 0.55rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--vt-c-accent);
  list-style: none;
  position: relative;
  user-select: none;
}
.marker-guide-summary::-webkit-details-marker {
  display: none;
}
.marker-guide-summary::after {
  content: '+';
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
  line-height: 1;
  transition: transform 0.15s ease;
}
.marker-guide[open] .marker-guide-summary::after {
  content: '−';
}
.marker-guide-body {
  padding: 0 0.8rem 0.75rem;
  color: #ddd;
}
.marker-guide-list {
  margin: 0;
  padding-left: 1.1rem;
  display: grid;
  gap: 0.3rem;
  font-size: 0.76rem;
  line-height: 1.35;
}
.marker-guide-list strong {
  color: var(--vt-c-accent);
}
.marker-guide-footnote {
  margin: 0.6rem 0 0;
  font-size: 0.72rem;
  opacity: 0.75;
}
.asset-preview-section {
  justify-content: flex-start;
  align-items: stretch;
  padding-top: var(--section-padding-block-start);
  --ar-preview-scale: 1;
}

.section-header {
  text-align: center;
  margin: 0 auto;
  width: min(100%, var(--base-layout-section-max-width));
}

.section-header h3 {
  font-size: var(--base-layout-section-heading-h3-size);
  color: var(--color-heading);
}

.section-header p {
  color: var(--color-text-muted);
  margin-top: 0.35rem;
}

.phone-preview-shell {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: var(--campaign-preview-phone-shell-margin-top);
  padding-bottom: var(--campaign-preview-phone-shell-padding-bottom);
}

.phone-preview {
  width: calc(var(--campaign-preview-phone-width) * var(--ar-preview-scale));
  max-width: 100%;
  background: #12162a;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: calc(var(--campaign-preview-phone-radius) * var(--ar-preview-scale));
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  padding: calc(var(--campaign-preview-phone-padding) * var(--ar-preview-scale));
}

.phone-speaker {
  width: 34%;
  height: var(--campaign-preview-phone-speaker-height);
  margin: 0 auto var(--campaign-preview-phone-speaker-margin-bottom);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
}

.phone-screen {
  --preview-zone-border: rgba(122, 230, 177, 0.95);
  --preview-zone-border-soft: rgba(122, 230, 177, 0.28);
  border-radius: calc(var(--campaign-preview-phone-screen-radius) * var(--ar-preview-scale));
  height: calc(var(--campaign-preview-phone-screen-height) * var(--ar-preview-scale));
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
  position: relative;
  padding: 0;
  width: 100%;
  background: #111827;
}

.phone-screen--asset {
  --asset-screen-inline-inset: var(--campaign-preview-phone-screen-padding-inline);
  --asset-screen-top-inset: var(--campaign-preview-phone-screen-padding-block);
  --asset-screen-bottom-inset: var(--campaign-preview-phone-screen-padding-block);
  --asset-screen-gap: var(--campaign-preview-phone-gap);
}

.scene-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top right;
  display: block;
}

.asset-overlay {
  position: absolute;
  inset:
    calc(var(--asset-screen-top-inset) + var(--campaign-preview-phone-logo-height) + var(--asset-screen-gap))
    var(--asset-screen-inline-inset)
    var(--asset-screen-bottom-inset);
  background: transparent;
  padding: clamp(0.85rem, 2.6vw, 1.05rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.9rem;
  min-height: 0;
  pointer-events: none;
  z-index: 1;
  border-radius: 1.1rem;
}

.asset-overlay-trigger {
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 1.1rem;
  overflow: visible;
  pointer-events: auto;
  cursor: pointer;
}

.asset-overlay-trigger:disabled {
  cursor: not-allowed;
}

.tap-target {
  position: absolute;
  z-index: 2;
  border: none;
  background: transparent;
  overflow: hidden;
}

.marching-ants {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.marching-ants rect {
  fill: none;
  stroke: var(--preview-zone-border);
  stroke-width: 1.6;
  stroke-dasharray: 2.2 2.2;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
  animation: marching-ants-offset 1.2s linear infinite;
}

@keyframes marching-ants-offset {
  to {
    stroke-dashoffset: -8.8;
  }
}


.asset-image {
  width: min(108%, 284px);
  max-height: 100%;
  object-fit: contain;
  transform: translateY(-1%);
  filter: drop-shadow(0 12px 18px rgba(2, 6, 23, 0.45));
}

.asset-chip,
.asset-placeholder {
  font-size: 0.96rem;
  color: #fff;
  text-align: center;
  padding: 0.62rem 0.9rem;
  border-radius: 0.8rem;
  border: 1px dashed rgba(122, 230, 177, 0.9);
  background: rgba(10, 17, 36, 0.65);
  max-width: min(84%, 260px);
}

.asset-placeholder {
  opacity: 0.9;
}

.asset-action-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: calc(var(--base-layout-modal-z-index) + 30);
  background: rgba(8, 12, 20, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.asset-action-modal {
  width: min(420px, 100%);
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 1rem;
  overflow: hidden;
}

.modal-header {
  padding: 0.95rem 1rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  position: relative;
}

.modal-header h3 {
  margin: 0;
  color: #f8fafc;
  text-align: center;
}

.modal-body {
  padding: 1rem;
}

.modal-body--asset-actions {
  display: grid;
  justify-items: center;
  text-align: center;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub {
  width: min(560px, 100%);
  max-height: 84vh;
  display: flex;
  flex-direction: column;
  background: var(--ui-inverse-surface) !important;
  border: 1px solid rgba(148, 163, 184, 0.24) !important;
  --ui-inverse-text: #ffffff;
}

.asset-action-modal--hub .modal-header,
.asset-action-modal--hub .modal-body {
  background: var(--ui-inverse-surface) !important;
}

.asset-action-modal--hub .modal-header {
  border-bottom: 1px solid rgba(148, 163, 184, 0.2) !important;
}

.asset-action-modal--hub .modal-body {
  overflow-y: auto;
  min-height: 0;
}

.modal-body--hub {
  display: grid;
  gap: 0.75rem;
}

.modal-body--asset-actions .modal-actions {
  width: 100%;
}

.modal-body p {
  margin: 0 0 0.8rem;
  color: #cbd5e1;
}

.modal-actions {
  display: grid;
  gap: 0.6rem;
}

.hub-block {
  display: grid;
  gap: 0.75rem;
}

.studio-block-title {
  margin: 0;
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 700;
}

.hub-block-copy {
  margin: 0;
  color: #ffffff !important;
}

.action-button {
  border: 1px solid var(--vt-c-accent);
  border-radius: 0.7rem;
  background: var(--vt-c-accent);
  color: var(--color-background);
  padding: 0.65rem 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.action-button:hover {
  border-color: var(--ui-inverse-cta-bg-hover);
  background: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
}

/* Short desktops / MacBooks — mirror the LookFeel section's 0.92 scale so
   the phone preview is visually consistent across steps, and shrink the
   section header so it doesn't hog the vertical space. */
@media (min-width: 769px) and (max-height: 900px) {
  .asset-preview-section {
    --ar-preview-scale: 0.82;
  }

  .section-header h3 {
    font-size: clamp(1.35rem, 2.2vw, 1.85rem);
  }

  .tracking-type-options {
    width: min(100%, 500px);
    margin-inline: auto;
  }

  .tracking-type-btn {
    padding: calc(0.55rem * 1) calc(0.9rem * 1);
    font-size: 0.95rem;
  }
}

@media (max-width: 768px) {
  .asset-overlay {
    inset:
      calc(var(--asset-screen-top-inset) + var(--campaign-preview-phone-logo-height) + var(--asset-screen-gap))
      0.9rem
      var(--asset-screen-bottom-inset);
    padding: 0.9rem;
  }

  .asset-image {
    width: min(234px, 75vw);
    max-height: 198px;
  }

  .asset-chip,
  .asset-placeholder {
    font-size: 0.82rem;
    padding: 0.48rem 0.68rem;
  }

  .asset-action-modal-overlay {
    align-items: flex-start;
    padding: var(--mobile-modal-overlay-top-padding) var(--mobile-modal-overlay-side-padding) var(--mobile-modal-overlay-bottom-padding);
  }
}

@media (max-width: 420px) {
  /* Small iPhones — section viewport is tight; everything above the preview
     gets aggressively trimmed so the phone preview stays inside the
     available height without clipping into the footer. Footer left alone. */
  .asset-preview-section {
    --ar-preview-scale: 0.78;
  }

  .phone-preview {
    width: clamp(132px, 42vw, 180px);
  }

  .phone-preview-shell {
    margin-top: calc(var(--campaign-preview-phone-shell-margin-top) * 0.35);
    padding-bottom: calc(var(--campaign-preview-phone-shell-padding-bottom) * 0.35);
  }

  .section-header {
    width: min(100%, 18rem);
  }

  .section-header h3 {
    font-size: clamp(0.98rem, 4.2vw, 1.15rem);
    line-height: 1.15;
  }

  .section-header p {
    margin-top: 0.15rem;
    font-size: 0.72rem;
    line-height: 1.25;
  }

  .ar-tracking-type-selector {
    margin-bottom: 0.55rem;
  }

  .tracking-type-label {
    font-size: 0.72rem;
    margin-bottom: 0.3rem;
  }

  .tracking-type-options {
    gap: 0.4rem;
  }

  .tracking-type-btn {
    padding: 0.4rem 0.5rem;
    gap: 0.1rem;
    border-radius: 0.65rem;
    max-width: none;
  }

  .tracking-type-btn-title {
    font-size: 0.8rem;
    font-weight: 700;
  }

  .tracking-type-btn-desc {
    font-size: 0.64rem;
    line-height: 1.15;
  }

  /* The 3D asset preview was using absolute px caps from the ≤768 rule,
     which didn't shrink alongside the smaller iPhone preview. Scale it
     down here; Samsung's 390–420 rule below bumps it back up. */
  .asset-image {
    width: min(158px, 54vw);
    max-height: 142px;
  }

  .asset-overlay {
    padding: 0.62rem;
  }

  .asset-chip,
  .asset-placeholder {
    font-size: 0.7rem;
    padding: 0.42rem 0.58rem;
    max-width: min(86%, 188px);
  }

  /* Small-iPhone shrink — tracking + action buttons, modal paddings,
     marker preview sizing. Intent: nothing wider than the screen,
     nothing taller than it needs to be. */
  .tracking-type-options {
    gap: 0.5rem;
  }
  .tracking-type-btn {
    padding: 0.5rem 0.55rem;
    border-radius: 10px;
  }
  .tracking-type-btn-title {
    font-size: 0.78rem;
  }
  .tracking-type-btn-desc {
    font-size: 0.66rem;
  }

  .marker-target-section {
    padding: 0 0.4rem;
  }
  .marker-upload-zone {
    padding: 1rem;
  }
  .marker-upload-placeholder {
    font-size: 0.78rem;
  }
  .marker-upload-icon {
    font-size: 1.5rem;
  }
  .marker-preview-container {
    gap: 0.65rem;
    padding: 0.55rem;
  }
  .marker-preview-image {
    width: 64px;
    height: 64px;
  }
  .marker-guide-summary {
    font-size: 0.78rem;
    padding: 0.5rem 0.7rem;
  }
  .marker-guide-list {
    font-size: 0.72rem;
  }

  .asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub {
    width: min(480px, 100%);
    max-height: calc(var(--mobile-modal-max-height, 100dvh) - 1rem);
  }
  .modal-header {
    padding: 0.65rem 0.8rem;
  }
  .modal-header h3 {
    font-size: 1rem;
  }
  .modal-close-button {
    width: 28px;
    height: 28px;
    font-size: 1.1rem;
  }
  .modal-body {
    padding: 0.75rem;
  }
  .modal-body--hub {
    gap: 0.55rem;
  }
  .hub-block {
    gap: 0.55rem;
  }
  .studio-block-title {
    font-size: 0.92rem;
  }
  .hub-block-copy {
    font-size: 0.8rem;
  }
  .modal-actions {
    gap: 0.45rem;
  }
  .action-button {
    padding: 0.55rem 0.7rem;
    font-size: 0.85rem;
    border-radius: 0.6rem;
  }
}

/* Larger small phones (Samsung, Pixel, larger iPhones) — bump preview back
   up to 0.98, matching the LookFeel section's 390–420 rule. iPhone SE and
   smaller keep the tighter 0.93 scale above. */
@media (min-width: 390px) and (max-width: 420px) {
  .asset-preview-section {
    --ar-preview-scale: 0.95;
  }

  .phone-preview {
    width: clamp(160px, 50vw, 210px);
  }

  .asset-image {
    width: min(210px, 66vw);
    max-height: 188px;
  }
}
</style>
